import { useState } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Check, Building2, Landmark, FileText, Zap } from "lucide-react";
import { usePublicPlans, useCheckout, usePlanUsage, type AdminPlan } from "@/hooks/use-plan-usage";
import { useAuth } from "@/providers/auth-provider";
import { useBusinesses } from "@/hooks/use-businesses";
import { useToast } from "@/hooks/use-toast";

export default function PricingPlansPage() {
  const [yearly, setYearly] = useState(false);
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const { activeBusiness } = useBusinesses();
  const { data: plans, isLoading } = usePublicPlans();
  const { data: usage } = usePlanUsage();
  const checkout = useCheckout();
  const { toast } = useToast();

  const handleSelect = (plan: AdminPlan) => {
    if (!user) {
      navigate("/auth/signup");
      return;
    }
    if (!activeBusiness) {
      toast({ title: "No business selected", description: "Create a business first.", variant: "destructive" });
      return;
    }

    const priceId = yearly ? plan.stripePriceIdYearly : plan.stripePriceIdMonthly;
    if (!priceId) {
      toast({ title: "Not available", description: `Stripe not configured for ${plan.name}. Contact support.`, variant: "destructive" });
      return;
    }

    checkout.mutate({
      planId: plan.id,
      billingCycle: yearly ? "yearly" : "monthly",
    });
  };

  const tierIcon = (name: string) => {
    if (name.includes("Seed")) return <Zap className="h-5 w-5" />;
    if (name.includes("Grow")) return <Building2 className="h-5 w-5" />;
    if (name.includes("Pro")) return <FileText className="h-5 w-5" />;
    return <Landmark className="h-5 w-5" />;
  };

  const isCurrentPlan = (planId: number) => usage?.planId === planId;

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-96 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const sortedPlans = (plans || []).filter((p) => p.isActive).sort((a, b) => parseFloat(a.monthlyPrice || "0") - parseFloat(b.monthlyPrice || "0"));

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold tracking-tight mb-2">Choose Your Plan</h1>
        <p className="text-muted-foreground max-w-lg mx-auto">
          Scale your bookkeeping with the plan that fits your firm.
        </p>

        <div className="flex items-center justify-center gap-3 mt-6">
          <span className={`text-sm font-medium ${!yearly ? "text-foreground" : "text-muted-foreground"}`}>Monthly</span>
          <Switch checked={yearly} onCheckedChange={setYearly} />
          <span className={`text-sm font-medium ${yearly ? "text-foreground" : "text-muted-foreground"}`}>
            Yearly <Badge variant="secondary" className="ml-1 text-xs">Save 17%</Badge>
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {sortedPlans.map((plan) => {
          const price = yearly ? plan.yearlyPrice : plan.monthlyPrice;
          const isCurrent = isCurrentPlan(plan.id);
          const isFree = parseFloat(price || "0") === 0;
          const features = Array.isArray(plan.features) ? plan.features : [];

          return (
            <Card
              key={plan.id}
              className={`relative flex flex-col ${isCurrent ? "border-primary ring-2 ring-primary/20" : ""}`}
            >
              {isCurrent && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">Current Plan</Badge>
              )}

              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 text-primary mb-1">
                  {tierIcon(plan.name)}
                  <CardTitle className="text-lg">{plan.name}</CardTitle>
                </div>
                {plan.description && (
                  <p className="text-sm text-muted-foreground">{plan.description}</p>
                )}
              </CardHeader>

              <CardContent className="flex-1">
                <div className="mb-6">
                  <span className="text-4xl font-bold">
                    {isFree ? "Free" : `$${parseFloat(price || "0").toFixed(0)}`}
                  </span>
                  {!isFree && (
                    <span className="text-muted-foreground text-sm">/{yearly ? "year" : "month"}</span>
                  )}
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span>{plan.maxBusinesses ?? 1} Business{(plan.maxBusinesses ?? 1) > 1 ? "es" : ""}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Landmark className="h-4 w-4 text-muted-foreground" />
                    <span>{plan.maxBankAccounts ?? 1} Bank Connection{(plan.maxBankAccounts ?? 1) > 1 ? "s" : ""}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span>{(plan.maxTransactions ?? 50).toLocaleString()} Transactions/mo</span>
                  </div>

                  {features.length > 0 && (
                    <div className="pt-3 border-t space-y-2">
                      {features.map((f, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                          <span>{typeof f === "string" ? f : String(f)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>

              <CardFooter>
                <Button
                  className="w-full"
                  variant={isCurrent ? "outline" : "default"}
                  disabled={isCurrent || checkout.isPending}
                  onClick={() => handleSelect(plan)}
                >
                  {isCurrent ? "Current Plan" : isFree ? "Get Started" : "Upgrade"}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
